import { useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, Plus, Trash2, Info, Mail, RefreshCw, Unlink, Check, Search } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AZ_OFFSET_MS = 7 * 60 * 60 * 1000;

const DS_SEL = "h-11 rounded-xl border border-black/10 bg-white px-2 text-sm text-brand-forest focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40";
const MONTHS = [["01", "Jan"], ["02", "Feb"], ["03", "Mar"], ["04", "Apr"], ["05", "May"], ["06", "Jun"], ["07", "Jul"], ["08", "Aug"], ["09", "Sep"], ["10", "Oct"], ["11", "Nov"], ["12", "Dec"]];

function DateSelect({ value, onChange, testid }) {
  const parse = (v) => {
    const [yy = "", mm = "", dd = ""] = (v || "").split("-");
    return { y: yy, m: mm, d: dd };
  };
  const [parts, setParts] = useState(parse(value));
  useEffect(() => {
    if (value) setParts(parse(value));
  }, [value]);
  const thisYear = new Date().getFullYear();
  const years = [thisYear - 1, thisYear, thisYear + 1, thisYear + 2];
  const commit = (next) => {
    setParts(next);
    onChange(next.y && next.m && next.d ? `${next.y}-${next.m}-${next.d}` : "");
  };
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <select value={parts.m} onChange={(e) => commit({ ...parts, m: e.target.value })} data-testid={`${testid}-month`} className={DS_SEL}>
        <option value="">Mon</option>
        {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <select value={parts.d ? String(Number(parts.d)) : ""} onChange={(e) => commit({ ...parts, d: e.target.value ? e.target.value.padStart(2, "0") : "" })} data-testid={`${testid}-day`} className={DS_SEL}>
        <option value="">Day</option>
        {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <select value={parts.y} onChange={(e) => commit({ ...parts, y: e.target.value })} data-testid={`${testid}-year`} className={DS_SEL}>
        <option value="">Year</option>
        {years.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} minute${m > 1 ? "s" : ""} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d > 1 ? "s" : ""} ago`;
}

function statusOf(r) {
  const now = new Date(Date.now() - AZ_OFFSET_MS); // approx Arizona "now" as naive
  const start = new Date(`${(r.checkin || "").slice(0, 10)}T14:00:00`);
  if (isNaN(start)) return { label: "—", cls: "bg-black/5 text-brand-forest/50" };
  if (now < start) return { label: "Upcoming", cls: "bg-blue-100 text-blue-700" };
  const co = (r.checkout || "").slice(0, 10);
  if (co) {
    const end = new Date(`${co}T11:00:00`);
    if (!isNaN(end) && now > end) return { label: "Past", cls: "bg-black/5 text-brand-forest/40" };
  }
  return { label: "Current", cls: "bg-green-100 text-green-700" };
}

function sourceLabel(s) {
  if (s === "airbnb") return "Airbnb";
  if (s === "vrbo") return "Vrbo";
  if (s === "direct") return "Direct booking";
  if (s === "hostshare") return "HostShare";
  if (s === "booking-site") return "Booking site";
  return "Manual";
}

const PLATFORMS = [
  { value: "airbnb", label: "Airbnb" },
  { value: "vrbo", label: "VRBO" },
  { value: "direct", label: "Direct booking" },
  { value: "hostshare", label: "HostShare" },
];

const ROW_TINTS = { airbnb: "bg-red-50", vrbo: "bg-blue-50", hostshare: "bg-green-50" };

export default function EditReservations() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ guest_name: "", phone: "", door_code: "", checkin: "", checkout: "", source: "direct" });
  const [saving, setSaving] = useState(false);
  const [codes, setCodes] = useState({});           // inline door-code edits keyed by reservation id
  const [checkouts, setCheckouts] = useState({});   // inline checkout-date edits keyed by reservation id
  const [checkins, setCheckins] = useState({});
  const [gmail, setGmail] = useState(null);
  const [importing, setImporting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [defaultCode, setDefaultCode] = useState("4524");
  const [openId, setOpenId] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = () => {
    api.get("/reservations").then((r) => {
      setItems(r.data);
      setCodes((prev) => Object.fromEntries(r.data.map((x) => [x.id, prev[x.id] ?? (x.door_code || "")])));
      setCheckouts((prev) => Object.fromEntries(r.data.map((x) => [x.id, prev[x.id] ?? (x.checkout || "").slice(0, 10)])));
      setCheckins((prev) => Object.fromEntries(r.data.map((x) => [x.id, prev[x.id] || (x.checkin || "").slice(0, 10)])));
    }).catch(() => {});
    api.get("/gmail/status").then((r) => setGmail(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      const dc = r.data?.default_door_code ?? "4524";
      setDefaultCode(dc);
      setForm((f) => (f.door_code ? f : { ...f, door_code: dc }));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Handle the OAuth redirect back from Google
  useEffect(() => {
    const g = params.get("gmail");
    if (!g) return;
    if (g === "connected") {
      toast.success("Gmail connected! Tap “Import bookings now” to pull your guests.");
    } else if (g === "error") {
      const reason = params.get("reason") || "";
      const map = {
        state_expired: "The sign-in took too long. Please tap Connect Gmail and try again.",
        token_exchange: "Google rejected the sign-in. If you just added the redirect URL, wait 2–3 minutes and try again.",
        no_refresh_token: "Google didn't grant offline access. Tap Connect Gmail again and be sure to allow all permissions.",
        access_denied: "You declined the permission. Tap Connect Gmail and allow access to import bookings.",
        no_code: "Google didn't complete the sign-in. Please try Connect Gmail again.",
      };
      toast.error(map[reason] || "Couldn’t connect Gmail. Please try again.");
    }
    params.delete("gmail");
    params.delete("reason");
    setParams(params, { replace: true });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectGmail = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/gmail/login");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start Gmail connection");
      setConnecting(false);
    }
  };

  const importBookings = async () => {
    setImporting(true);
    try {
      const r = await api.post("/gmail/import");
      if (r.data.connected === false) {
        toast.error("Your Gmail link has expired or was removed. Tap “Connect Gmail” to link it again, then import.");
        load();
        return;
      }
      const { created, existing, unparsed, updated = 0 } = r.data;
      if (created > 0) toast.success(`Imported ${created} new booking${created === 1 ? "" : "s"} (door code set to ${defaultCode || "4524"}). Change any individually below.`);
      else if (updated > 0) toast.success(`No new bookings, but corrected the dates on ${updated} existing one${updated === 1 ? "" : "s"}.`);
      else toast.info(existing > 0 ? "No new bookings — everything is already imported." : "No booking emails found to import.");
      if (created > 0 && updated > 0) toast.message(`Also corrected dates on ${updated} existing booking${updated === 1 ? "" : "s"}.`);
      if (unparsed > 0) toast.message(`${unparsed} email${unparsed === 1 ? "" : "s"} couldn’t be read automatically — add those manually if needed.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  const disconnectGmail = async () => {
    await api.post("/gmail/disconnect");
    toast.success("Gmail disconnected");
    setScan(null);
    load();
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.get("/gmail/scan");
      setScan(r.data);
      if ((r.data.results || []).length === 0) toast.info("No Airbnb/Vrbo emails were found (searched everywhere, last 2 years). Double-check the connected Google account is the one that receives them.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally { setScanning(false); }
  };

  const add = async () => {
    if (!form.guest_name.trim() || !form.checkin) { toast.error("First name and check-in (move-in) date are required."); return; }
    if (!form.door_code.trim()) { toast.error("Enter the door code for this guest."); return; }
    setSaving(true);
    try {
      await api.post("/reservations", form);
      toast.success("Reservation added");
      setForm({ guest_name: "", phone: "", door_code: defaultCode, checkin: "", checkout: "", source: "direct" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add reservation");
    } finally { setSaving(false); }
  };

  const saveCode = async (r) => {
    try {
      await api.put(`/reservations/${r.id}`, {
        guest_name: r.guest_name, phone: r.phone || "", door_code: codes[r.id] || "",
        checkin: r.checkin, checkout: r.checkout,
      });
      toast.success("Door code saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save door code");
    }
  };

  const saveCheckout = async (r) => {
    const co = checkouts[r.id] || "";
    if (co && co <= (r.checkin || "").slice(0, 10)) { toast.error("Checkout must be after the check-in date."); return; }
    try {
      await api.put(`/reservations/${r.id}`, {
        guest_name: r.guest_name, phone: r.phone || "", door_code: codes[r.id] ?? (r.door_code || ""),
        checkin: r.checkin, checkout: co,
      });
      toast.success(co ? "Checkout date saved" : "Checkout cleared — access ends at the next booking");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save checkout date");
    }
  };

  const saveCheckin = async (r) => {
    const ci = checkins[r.id] || "";
    if (!ci) { toast.error("Pick a full check-in date (month, day and year)."); return; }
    const co = (r.checkout || "").slice(0, 10);
    if (co && ci >= co) { toast.error("Check-in must be before the check-out date. Change the check-out first."); return; }
    try {
      await api.put(`/reservations/${r.id}`, {
        guest_name: r.guest_name, phone: r.phone || "", door_code: codes[r.id] ?? (r.door_code || ""),
        checkin: ci, checkout: r.checkout || "",
      });
      toast.success("Check-in date saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save check-in date");
    }
  };

  const restore = async (r) => {
    try {
      await api.post("/reservations/restore", r);
      toast.success(`Restored ${r.guest_name || "the"} booking`);
      load();
    } catch (e) {
      toast.error("Couldn't undo — please re-add the booking manually.");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the reservation for ${r.guest_name || "this guest"}?`)) return;
    await api.delete(`/reservations/${r.id}`);
    toast.success("Reservation removed", {
      action: { label: "Undo", onClick: () => restore(r) },
      duration: 8000,
    });
    load();
  };

  const connected = gmail?.connected;

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <div>
        <span className="overline text-brand-forest/50">Door Code Access</span>
        <h1 className="font-display text-3xl tracking-tight mt-1">Reservations</h1>
        <p className="text-brand-forest/60 mt-2 max-w-2xl">
          Guests unlock their door code by entering their <strong>first name</strong> (as entered below). It reveals the <strong>Door Code</strong> from their check-in (2:00 PM, Arizona time) and <strong>automatically stops working when the next guest's check-in date arrives</strong> — so only one guest can see the code at a time. A checkout date is optional.
        </p>
      </div>

      {/* Gmail auto-import */}
      <div className="bg-white border border-black/5 shadow-sm rounded-2xl p-5" data-testid="gmail-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-brand-sand flex items-center justify-center shrink-0">
            <Mail size={20} className="text-brand-terracotta" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-xl">Import guests from your booking emails</h2>
            {!connected ? (
              <>
                <p className="text-sm text-brand-forest/70 mt-1 max-w-2xl">
                  Connect the Gmail inbox that receives your Airbnb &amp; Vrbo confirmation emails. We’ll read those emails and add each guest’s <strong>first name</strong> and stay dates here automatically. You’ll just add a door code to each. We only read booking emails — nothing is ever changed or sent.
                </p>
                <Button onClick={connectGmail} disabled={connecting} data-testid="gmail-connect-btn"
                  className="mt-4 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white transition-colors">
                  <Mail size={16} className="mr-2" /> {connecting ? "Opening Google…" : "Connect Gmail"}
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mt-1 text-sm" data-testid="gmail-connected-status">
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                  <span className="text-brand-forest/80">
                    Connected as <strong>{gmail.email || "your Gmail"}</strong>
                    {gmail.last_import_at ? ` · last imported ${timeAgo(gmail.last_import_at)}` : ""}
                  </span>
                </div>
                <p className="text-sm text-brand-forest/60 mt-1">
                  We scan Airbnb &amp; Vrbo confirmation emails from the last 2 years. Run it any time new bookings come in.
                </p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button onClick={importBookings} disabled={importing} data-testid="gmail-import-btn"
                    className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white transition-colors">
                    <RefreshCw size={16} className={`mr-2 ${importing ? "animate-spin" : ""}`} />
                    {importing ? "Importing…" : "Import bookings now"}
                  </Button>
                  <Button onClick={disconnectGmail} variant="outline" data-testid="gmail-disconnect-btn"
                    className="rounded-full border-black/10 text-brand-forest/70 hover:text-red-600">
                    <Unlink size={16} className="mr-2" /> Disconnect
                  </Button>
                </div>
                <button onClick={runScan} disabled={scanning} data-testid="gmail-scan-btn"
                  className="mt-3 text-sm text-brand-terracotta hover:underline inline-flex items-center gap-1.5 disabled:opacity-50">
                  <Search size={14} /> {scanning ? "Scanning your inbox…" : "See what's in my inbox (diagnostics)"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Scan diagnostics */}
      {scan && (
        <div className="bg-white border border-black/5 shadow-sm rounded-2xl p-5" data-testid="gmail-scan-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display text-lg">Inbox scan results</h3>
            <button onClick={() => setScan(null)} className="text-sm text-brand-forest/50 hover:text-brand-forest">Hide</button>
          </div>
          <p className="text-sm text-brand-forest/70 mt-1">
            Found <strong>{scan.scanned}</strong> Airbnb/Vrbo email{scan.scanned === 1 ? "" : "s"} ·{" "}
            <span className="text-green-700 font-medium">{scan.results.filter((r) => ["ready", "imported"].includes(r.status)).length} readable</span> ·{" "}
            <span className="text-brand-forest/60">{scan.results.filter((r) => r.status === "existing").length} already added</span> ·{" "}
            <span className="text-red-600 font-medium">{scan.unparsed} couldn’t be read</span>
          </p>
          <p className="text-xs text-brand-forest/50 mt-1">
            The ones marked “couldn’t read” tell me which email formats to teach the reader — copy a couple of those subject lines to me and I’ll fix them.
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs" data-testid="gmail-scan-table">
              <thead className="text-brand-forest/50 text-left border-b border-black/5">
                <tr>
                  <th className="py-2 pr-3 font-semibold">Result</th>
                  <th className="py-2 pr-3 font-semibold">Subject</th>
                  <th className="py-2 pr-3 font-semibold">Guest / reason</th>
                  <th className="py-2 pr-3 font-semibold">Dates</th>
                </tr>
              </thead>
              <tbody>
                {scan.results.map((r, i) => {
                  const badge = {
                    imported: "bg-green-100 text-green-700",
                    ready: "bg-green-100 text-green-700",
                    existing: "bg-black/5 text-brand-forest/50",
                    skipped: "bg-red-50 text-red-600",
                  }[r.status] || "bg-black/5";
                  const label = { imported: "Imported", ready: "Readable", existing: "Already added", skipped: "Couldn’t read" }[r.status] || r.status;
                  return (
                    <tr key={i} className="border-b border-black/5 align-top" data-testid={`gmail-scan-row-${i}`}>
                      <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 font-semibold whitespace-nowrap ${badge}`}>{label}</span></td>
                      <td className="py-2 pr-3 max-w-sm">
                        <div className="truncate" title={r.subject}>{r.subject || "—"}</div>
                        <div className="text-brand-forest/40 truncate">{r.from}</div>
                        {r.snippet && (
                          <details className="mt-1">
                            <summary className="cursor-pointer text-brand-terracotta/80 text-[11px]">show email text</summary>
                            <div className="mt-1 p-2 bg-black/5 rounded text-[11px] text-brand-forest/70 whitespace-pre-wrap break-words max-w-md" data-testid={`gmail-scan-snippet-${i}`}>{r.snippet}</div>
                          </details>
                        )}
                      </td>
                      <td className="py-2 pr-3">{r.guest_name ? <strong>{r.guest_name}</strong> : <span className="text-brand-forest/50 italic">{r.reason || "—"}</span>}</td>
                      <td className="py-2 pr-3 whitespace-nowrap text-brand-forest/70">{r.checkin ? `${r.checkin} → ${r.checkout}` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-brand-sand/60 border border-brand-terracotta/20 rounded-2xl p-4 flex gap-3" data-testid="reservations-help">
        <Info size={18} className="text-brand-terracotta shrink-0 mt-0.5" />
        <p className="text-sm text-brand-forest/80">
          Imported and new reservations are pre-filled with your <strong>default door code ({defaultCode || "4524"})</strong>. Change any individual guest's code in the table below, or update the default in <strong>Settings</strong>.
        </p>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
        <h2 className="font-display text-xl mb-4">Add a reservation manually</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="mb-1.5 block">First Name <span className="text-brand-forest/40">(guest unlocks with this)</span></Label>
            <Input data-testid="res-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="Jane" />
          </div>
          <div>
            <Label className="mb-1.5 block">Phone <span className="text-brand-forest/40">(optional)</span></Label>
            <Input data-testid="res-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(480) 555-0142" />
          </div>
          <div>
            <Label className="mb-1.5 block">Door Code</Label>
            <Input data-testid="res-door-code" value={form.door_code} onChange={(e) => setForm({ ...form, door_code: e.target.value })} placeholder="e.g. 5280" />
          </div>
          <div>
            <Label className="mb-1.5 block">Check-in Date</Label>
            <DateSelect testid="res-checkin" value={form.checkin} onChange={(v) => setForm({ ...form, checkin: v })} />
          </div>
          <div>
            <Label className="mb-1.5 block">Check-out Date <span className="text-brand-forest/40">(optional)</span></Label>
            <DateSelect testid="res-checkout" value={form.checkout} onChange={(v) => setForm({ ...form, checkout: v })} />
          </div>
          <div>
            <Label className="mb-1.5 block">Booked on</Label>
            <select
              data-testid="res-platform"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm"
            >
              {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>
        <Button onClick={add} disabled={saving} data-testid="res-add" className="mt-4 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white transition-colors">
          <Plus size={16} className="mr-1" /> {saving ? "Adding…" : "Add Reservation"}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }} data-testid="reservations-scroll">
        {items.length === 0 ? (
          <div className="p-10 text-center text-brand-forest/50 flex flex-col items-center gap-2">
            <CalendarDays size={28} /> No reservations yet.
          </div>
        ) : (
          <table className="w-full min-w-[1100px] text-sm" data-testid="reservations-table">
            <thead className="bg-brand-bg text-brand-forest/50 text-left">
              <tr>
                <th className="px-5 py-3 font-semibold text-center w-12">#</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold sticky left-0 z-10 bg-brand-bg shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">Unlocks with (first name)</th>
                <th className="px-5 py-3 font-semibold">Door Code</th>
                <th className="px-5 py-3 font-semibold">Check-in</th>
                <th className="px-5 py-3 font-semibold">Check-out</th>
                <th className="px-5 py-3 font-semibold">Source</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => {
                const st = statusOf(r);
                const first = (r.guest_name || "").trim().split(" ")[0] || "—";
                const dirty = (codes[r.id] || "") !== (r.door_code || "");
                const coDirty = (checkouts[r.id] || "") !== (r.checkout || "").slice(0, 10);
                const ciDirty = (checkins[r.id] || "") !== (r.checkin || "").slice(0, 10);
                const rowTint = ROW_TINTS[r.source] || "";
                return (
                  <Fragment key={r.id}>
                  <tr className={`border-t border-black/5 ${rowTint}`} data-testid={`res-row-${r.id}`}>
                    <td className="px-5 py-3 text-center font-semibold text-brand-forest/50 tabular-nums" data-testid={`res-number-${i}`}>{i + 1}</td>
                    <td className="px-5 py-3"><span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${st.cls}`}>{st.label}</span></td>
                    <td className={`px-5 py-3 font-semibold text-brand-forest/80 sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] ${rowTint || "bg-white"}`} data-testid={`res-name-cell-${r.id}`}>
                      <button
                        type="button"
                        onClick={() => setOpenId(openId === r.id ? null : r.id)}
                        data-testid={`res-name-toggle-${r.id}`}
                        className="inline-flex items-center gap-1.5 text-left hover:text-brand-terracotta transition-colors"
                        title="Tap to see booking dates"
                      >
                        {first}
                        <CalendarDays size={13} className="text-brand-forest/30" />
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <Input
                          value={codes[r.id] ?? ""}
                          onChange={(e) => setCodes({ ...codes, [r.id]: e.target.value })}
                          placeholder="add code"
                          data-testid={`res-door-code-input-${r.id}`}
                          className={`h-9 w-28 font-mono tracking-widest ${!r.door_code ? "border-brand-terracotta/60 bg-brand-sand/40" : ""}`}
                        />
                        <button
                          onClick={() => saveCode(r)}
                          disabled={!dirty}
                          data-testid={`res-door-code-save-${r.id}`}
                          className={`h-9 w-9 flex items-center justify-center rounded-lg transition-colors ${dirty ? "bg-brand-forest text-white hover:bg-brand-terracotta" : "bg-black/5 text-brand-forest/30"}`}
                          title="Save door code"
                        >
                          <Check size={16} />
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <DateSelect
                          value={checkins[r.id] ?? ""}
                          onChange={(v) => setCheckins({ ...checkins, [r.id]: v })}
                          testid={`res-checkin-input-${r.id}`}
                        />
                        <button
                          onClick={() => saveCheckin(r)}
                          disabled={!ciDirty}
                          data-testid={`res-checkin-save-${r.id}`}
                          className={`h-11 w-11 flex items-center justify-center rounded-lg transition-colors ${ciDirty ? "bg-brand-forest text-white hover:bg-brand-terracotta" : "bg-black/5 text-brand-forest/30"}`}
                          title="Save check-in date"
                        >
                          <Check size={16} />
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <DateSelect
                          value={checkouts[r.id] ?? ""}
                          onChange={(v) => setCheckouts({ ...checkouts, [r.id]: v })}
                          testid={`res-checkout-input-${r.id}`}
                        />
                        <button
                          onClick={() => saveCheckout(r)}
                          disabled={!coDirty}
                          data-testid={`res-checkout-save-${r.id}`}
                          className={`h-11 w-11 flex items-center justify-center rounded-lg transition-colors ${coDirty ? "bg-brand-forest text-white hover:bg-brand-terracotta" : "bg-black/5 text-brand-forest/30"}`}
                          title="Save checkout date"
                        >
                          <Check size={16} />
                        </button>
                      </div>
                      {!(r.checkout || "").slice(0, 10) && <span className="text-[11px] text-brand-forest/40 italic">blank = ends at next guest</span>}
                    </td>
                    <td className="px-5 py-3 text-brand-forest/50">{sourceLabel(r.source)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => remove(r)} data-testid={`res-delete-${r.id}`} className="text-brand-forest/40 hover:text-red-500 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                  {openId === r.id && (
                    <tr className={`border-t border-black/5 ${rowTint}`} data-testid={`res-dates-row-${r.id}`}>
                      <td className="px-5 py-3" />
                      <td className="px-5 pb-4 pt-1 sticky left-0" colSpan={7}>
                        <div className="max-w-[calc(100vw-9rem)] lg:max-w-none rounded-xl bg-brand-sand/50 border border-brand-terracotta/15 px-4 py-3 flex flex-wrap items-end gap-x-8 gap-y-3 text-sm">
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-brand-forest/40">Check-in</div>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <DateSelect
                                value={checkins[r.id] ?? ""}
                                onChange={(v) => setCheckins({ ...checkins, [r.id]: v })}
                                testid={`res-checkin-input-m-${r.id}`}
                              />
                              <button
                                onClick={() => saveCheckin(r)}
                                disabled={!ciDirty}
                                data-testid={`res-checkin-save-m-${r.id}`}
                                className={`h-11 px-4 flex items-center gap-1.5 rounded-lg text-sm font-semibold transition-colors ${ciDirty ? "bg-brand-forest text-white hover:bg-brand-terracotta" : "bg-black/5 text-brand-forest/30"}`}
                                title="Save check-in date"
                              >
                                <Check size={15} /> Save
                              </button>
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-brand-forest/40">Move-out (check-out)</div>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <DateSelect
                                value={checkouts[r.id] ?? ""}
                                onChange={(v) => setCheckouts({ ...checkouts, [r.id]: v })}
                                testid={`res-checkout-input-m-${r.id}`}
                              />
                              <button
                                onClick={() => saveCheckout(r)}
                                disabled={!coDirty}
                                data-testid={`res-checkout-save-m-${r.id}`}
                                className={`h-11 px-4 flex items-center gap-1.5 rounded-lg text-sm font-semibold transition-colors ${coDirty ? "bg-brand-forest text-white hover:bg-brand-terracotta" : "bg-black/5 text-brand-forest/30"}`}
                                title="Save move-out date"
                              >
                                <Check size={15} /> Save
                              </button>
                            </div>
                            {!(r.checkout || "").slice(0, 10) && <span className="text-[11px] text-brand-forest/40 italic">Blank = access ends when the next guest checks in</span>}
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-brand-forest/40">Source</div>
                            <div className="font-semibold text-brand-forest/80">{sourceLabel(r.source)}</div>
                          </div>
                          <button
                            onClick={() => remove(r)}
                            data-testid={`res-delete-m-${r.id}`}
                            className="h-11 px-4 flex items-center gap-1.5 rounded-lg text-sm font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-600 hover:text-white transition-colors"
                          >
                            <Trash2 size={15} /> Delete reservation
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
